import { MedelyDateTime } from '@medely/date-time-tools';
import { yup } from '@medely/ui-kit';

type ValidateEndDateProps = {
  startDate: Date;
  endDate: Date;
};

export const validateEndDate = ({ startDate, endDate }: ValidateEndDateProps) => {
  if (!endDate) {
    return 'Required';
  }

  const parsedStartDate = new MedelyDateTime(new Date(startDate).toISOString(), { tz: 'system' });
  const parsedEndDate = new MedelyDateTime(new Date(endDate).toISOString(), { tz: 'system' });
  const today = new MedelyDateTime(new Date().toISOString(), { tz: 'system' });
  const isEndDateAfterStartDate = parsedEndDate.isAfter(parsedStartDate);
  const isEndDateInTheFuture = parsedEndDate.isBefore(today);

  if (!isEndDateAfterStartDate) {
    return 'End date must be after start date';
  }

  if (!isEndDateInTheFuture) {
    return 'End date cannot be in the future';
  }

  return undefined;
};

const validateEndDateField = () =>
  yup.mixed().test({
    name: 'valid-end-date',
    test(value, ctx) {
      if (ctx.parent.currently_work_here) {
        return true;
      }

      // The required validation is handled by the .required() call in the schema
      // This test only handles the date validation logic
      const errorMessage = validateEndDate({
        endDate: value as Date,
        startDate: ctx.parent.start_date,
      });
      if (!!errorMessage) {
        return ctx.createError({ message: errorMessage });
      }
      return true;
    },
  });

const workHistoryValidations = {
  currently_work_here: yup.boolean(),
  start_date: yup.date().required('Required'),
  end_date: yup
    .mixed()
    .nullable()
    .when('currently_work_here', {
      is: (value: boolean) => !value,
      then: (schema) => schema.required('Required').concat(validateEndDateField()),
    }),
  employment_type: yup.number().required('Required'),
  organization_name: yup.string().required('Required'),
  location: yup.string().required('Required'),
  state_id: yup.number().required('Required'),
};

export const workHistoryClinicalValidationSchema = yup.object().shape({
  ...workHistoryValidations,
  position_id: yup.number().required('Required'),
  position_title: yup.string().nullable(),
  unit: yup.string().required('Required'),
  floating: yup.boolean(),
  floating_details: yup
    .string()
    .nullable()
    .when('floating', { is: true, then: (schema) => schema.required('Required') }),
  hours_per_week: yup.number().nullable(),
  description: yup.string().required('Required'),
  location_type_id: yup.number().required('Required'),
  location_trauma_level: yup
    .string()
    .nullable()
    .when('location_type_id', {
      is: 8,
      then: (schema) => schema.required('Required'),
    }),
  location_number_of_beds: yup
    .number()
    .nullable()
    .when('location_type_id', {
      is: 8,
      then: (schema) => schema.required('Required').min(1, 'Must be at least 1'),
    }),
  location_is_teaching: yup
    .boolean()
    .nullable()
    .when('location_type_id', {
      is: 8,
      then: (schema) => schema.required('Required'),
    }),
});

export const workHistoryNonClinicalValidationSchema = yup.object().shape({
  ...workHistoryValidations,
  position_title: yup.string().required('Required'),
  description: yup.string().required('Required'),
});
